import * as Yup from "yup";
import { t } from "i18next";
import Box from "@mui/material/Box";
import Link from "@mui/material/Link";
import Grid from "@mui/material/Grid";
import { jwtDecode } from "jwt-decode";
import { Divider } from "@mui/material";
import { useForm } from "react-hook-form";
import Button from "@mui/material/Button";
import { useEffect, useState } from "react";
import Snackbar from "@mui/material/Snackbar";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogActions from "@mui/material/DialogActions";
import { AppStandardButton } from "../../UI/AppButtons";
import { useNavigate } from "react-router-dom";
import TextField from "@mui/material/TextField";
import Container from "@mui/material/Container";
import { CircularProgress } from "@mui/material";
import { GoogleLogin } from "@react-oauth/google";
import { yupResolver } from "@hookform/resolvers/yup";
import BookingApi from "../../../API/Apis/BookingApi";
import { formatTheme } from "../../../config/MuiColorThemes";
import { createTheme, ThemeProvider } from "@mui/material/styles";
import { BookingGetters } from "../../../store/onlineBooking/getters";
import { useBackgroundLoader } from "../../UI/BgReservedArea/BackgroundLoader";
import NotAuthorisedPage from "../../../views/notAuthorisedPage";
import { useTranslation } from "react-i18next";
import i18n from "../../../config/i18n";

export default function SignIn() {
  const navigate = useNavigate();
  const dynamicPalette = formatTheme();
  const dynamicTheme = createTheme(dynamicPalette);
  const validationSchema = Yup.object().shape({
    email: Yup.string()
      .required(t("Email is required"))
      .email(t("Email is invalid")),
    password: Yup.string()
      .required(t("Password is required"))
      .min(6, t("Password must be at least 6 characters")),
  });
  const appSettings = BookingGetters.getAppSettings();
  const requireManualApprovalForNewRegistrations =
    appSettings?.requireManualApprovalForNewRegistrations;

  const [showReserve, setShowReserve] = useState<boolean>(
    !!appSettings?.reservedArea
  );
  const [reserveChecked, setReserveChecked] = useState<boolean>(false);
  const [loader, setLoader] = useState<boolean>(false);

  const [errorToast, setErrorToast] = useState({
    open: false,
    message: "",
    type: "",
  });
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const { t: translate } = useTranslation();
  const lang = i18n.language;
  const textResource = BookingGetters.getTextResourceDetailsBasedOnLang();
  const data = textResource?.[lang]?.flatMap((item) => item.data || []) || [];
  const [underReviewMessage, setUnderReviewMessage] = useState("");
  useEffect(() => {
    const msgData = data.find(
      (item) => item.key === "registrationUnderReviewText"
    );
    setUnderReviewMessage(
      msgData?.body ||
        translate("Thank you! Your registration is under review.")
    );
  }, [textResource, lang]);
  const reservedAreaBgImageUrl = appSettings?.config?.reservedAreaBgImage;
  const defaultImage = "/assets/images/reserve_area_car.jpg";
  const { getImage, backgroundStyle } = useBackgroundLoader({
    imageUrl: reservedAreaBgImageUrl,
    defaultImage,
  });
  const handleSignupNavigation = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    navigate("/signup");
  };

  const handleForgotPasswordNavigation = (e: {
    preventDefault: () => void;
  }) => {
    e.preventDefault();
    navigate("/forgotpassword");
  };

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<{ remember: boolean; email: string; password: string }>({
    resolver: yupResolver(validationSchema),
  });

  const onSubmit = async (data: {
    remember: boolean;
    email: string;
    password: string;
  }) => {
    try {
      setLoader(true);

      if (data.remember) {
        localStorage.setItem("email", data.email);
        localStorage.setItem("password", data.password);
      } else {
        localStorage.removeItem("email");
        localStorage.removeItem("password");
      }

      const response = await BookingApi.loginUser(data);
      const result = response?.data;
      if (result.status === "success") {
        localStorage.setItem("userID", result?.user.id);
        localStorage.setItem("isUserLoggedin", "true");
        localStorage.setItem("userAffiliate", result?.user?.affiliate);
        localStorage.setItem("customerId", result?.user?.attachedCust);
        localStorage.setItem(
          "onlineUserType",
          result?.onlineUserType?.description
        );

        navigate("/");
      }
      if (result.msg === "Account Not Registered with this Email ID") {
        setErrorToast({
          open: true,
          message: t("Email not found. Please sign up first."),
          type: "error",
        });
      } else if (requireManualApprovalForNewRegistrations) {
        setReviewDialogOpen(true);
      } else {
        setErrorToast({
          open: true,
          message: result.msg || t("Password does not match"),
          type: "error",
        });
      }
    } catch (error) {
      console.error(error);
      setErrorToast({
        open: true,
        message: t("Something went wrong! Try again."),
        type: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  // ✅ Google Login Logic
  const handleGoogleSuccess = async (
    credentialResponse: any
  ): Promise<void> => {
    try {
      setLoader(true);
      const decoded = jwtDecode<{ email: string; sub: string }>(
        credentialResponse.credential
      );
      const data = {
        email: decoded.email?.toLowerCase(),
        password: decoded.sub,
      };
      console.log("data", data);
      localStorage.setItem("email", data.email);
      localStorage.setItem("password", data.password);

      const response = await BookingApi.loginUser(data);

      if (response.data.status === "success") {
        localStorage.setItem("userID", response.data.user.id);
        localStorage.setItem("isUserLoggedin", "true");
        localStorage.setItem("userAffiliate", response?.data?.user?.affiliate);
        localStorage.setItem(
          "onlineUserType",
          response?.data?.onlineUserType?.description
        );
        navigate("/");
      } else {
        setErrorToast({
          open: true,
          message: t("Email not found on Google. Try manual sign-in."),
          type: "error",
        });
      }
    } catch (error) {
      setErrorToast({
        open: true,
        message: t("Something went wrong. Please try again."),
        type: "error",
      });
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    BookingApi.getAppSetting().then((e: any) => {
      setShowReserve(e.reservedArea);
      setReserveChecked(true);
    });
  }, []);

  useEffect(() => {
    const getUser = localStorage.getItem("userID");
    if (getUser) {
      navigate("/");
    }
  });

  if (!reserveChecked) return null;
  if (!showReserve) return <NotAuthorisedPage />;

  const hideSignUpButton = appSettings?.hideSignUpButton;
  return (
    <ThemeProvider theme={dynamicTheme}>
      {loader && (
        <div className="fixed inset-0 z-[2000] flex items-center justify-center bg-black/40 backdrop-blur-sm">
          <CircularProgress sx={{ color: "white" }} />
        </div>
      )}
      <div className="relative min-h-screen flex items-center justify-center bg-transparent">
        {/* Background Image  mobile */}
        {getImage && (
          <div
            style={backgroundStyle}
            className="absolute inset-0 w-full h-full object-cover lg:hidden z-0"
          />
        )}
        <div className="absolute inset-0  md:hidden z-0" />
        <div className="grid grid-cols-1 lg:grid-cols-2 w-full min-h-screen">
          {/* Left Image (Desktop Only) */}
          <div className="hidden lg:block w-full h-screen overflow-hidden">
            {!getImage ? (
              <div className="absolute top-0 left-0 w-1/2 h-full flex items-center justify-center z-10">
                <CircularProgress sx={{ color: "gray" }} />
              </div>
            ) : (
              <div className="w-full h-full object-cover">
                <div
                  style={backgroundStyle}
                  className="w-full h-full object-cover"
                />
              </div>
            )}
          </div>

          {/* Right Login Section */}
          <div className="relative z-10 flex justify-center items-center max-h-screen lg:bg-gray-100">
            <div className="bg-white shadow-lg rounded-2xl px-9 py-11 w-full max-w-[22rem] md:max-w-[25rem] mx-4 border-2">
              <h1
                className="text-center text-2xl font-semibold mb-6"
                style={{ color: dynamicTheme?.palette["primary"]?.main }}
              >
                {t("Sign in to your account")}
              </h1>

              {/* Form */}
              <Container component="main" disableGutters>
                <Grid container justifyContent="center">
                  <Box
                    component="form"
                    onSubmit={handleSubmit(onSubmit)}
                    noValidate
                    sx={{
                      width: "100%",
                      "& .MuiTextField-root": {
                        mt: 2,
                        width: "100%",
                        backgroundColor: "white",
                      },
                    }}
                  >
                    {/* Email */}
                    <TextField
                      required
                      fullWidth
                      id="email"
                      label={t("Email_Address")}
                      autoComplete="off"
                      {...register("email")}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      FormHelperTextProps={{
                        sx: {
                          textAlign: "left",
                          ml: 0,
                        },
                      }}
                    />

                    {/* Password */}
                    <TextField
                      required
                      fullWidth
                      label={t("Password")}
                      type="password"
                      id="password"
                      autoComplete="off"
                      {...register("password")}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                      FormHelperTextProps={{
                        sx: {
                          textAlign: "left",
                          ml: 0,
                        },
                      }}
                    />

                    {/* Button */}
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      sx={{
                        mt: 3,
                        mb: 2,
                        fontWeight: "bold",
                        fontSize: "17px",
                        p: 1,
                        textTransform: "none",
                      }}
                    >
                      {t("Login")}
                    </Button>
                    <Divider sx={{ bgcolor: "grey.300", mt: 2, mb: 4 }} />
                    {/* Google */}
                    {/* <div className="flex justify-center mb-4 mt-4">
                      <GoogleLogin
                        onSuccess={handleGoogleSuccess}
                        onError={() => alert("Login Failed")}
                      />
                    </div> */}

                    {/* Links */}
                    <Grid container direction="column" alignItems="center">
                      <Grid item>
                        <Link
                          onClick={handleForgotPasswordNavigation}
                          component="button"
                          underline="none"
                          sx={{
                            fontWeight: 600,
                            fontSize: "12px",
                            lineHeight: "100%",
                            letterSpacing: 0,
                          }}
                        >
                          {t("Forgot_password")}?
                        </Link>
                      </Grid>
                      {!hideSignUpButton && (
                        <Grid item>
                          <Link
                            onClick={handleSignupNavigation}
                            component="button"
                            underline="none"
                            sx={{
                              fontWeight: 600,
                              fontSize: "12px",
                              lineHeight: "100%",
                              letterSpacing: 0,
                            }}
                          >
                            {t("Don't have an account? Sign up")}
                          </Link>
                        </Grid>
                      )}
                    </Grid>
                  </Box>
                </Grid>
              </Container>
            </div>
          </div>
        </div>
      </div>
      {/* REGISTRATION UNDER REVIEW DIALOG */}
      <Dialog
        open={reviewDialogOpen}
        onClose={() => setReviewDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        PaperProps={{ style: { borderRadius: "20px" } }}
      >
        <DialogTitle style={{ padding: "3rem" }}>
          {underReviewMessage}
        </DialogTitle>
        <DialogActions>
          <AppStandardButton
            variant="outlined"
            onClick={() => setReviewDialogOpen(false)}
          >
            <p className="text-black normal-case">{t("Close")}</p>
          </AppStandardButton>
        </DialogActions>
      </Dialog>
      {/* ERROR TOAST */}
      <Snackbar
        open={errorToast.open}
        autoHideDuration={6000}
        onClose={() => setErrorToast({ ...errorToast, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        ContentProps={{
          sx: {
            backgroundColor:
              errorToast.type === "warning" ? "#ed6c02" : "#d32f2f",
            color: "white",
            fontWeight: "bold",
            fontSize: "14px",
          },
        }}
        message={errorToast.message}
      />
    </ThemeProvider>
  );
}
